import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, Upload, FileSpreadsheet, CheckCircle2 } from 'lucide-react';

const enconOrange = '#ee7203';
const enconYellow = '#fbbf24';
const b29Gradient = `linear-gradient(90deg, ${enconOrange} 0%, ${enconYellow} 100%)`;

/** Settings > Pricing > B29 — single email address used by the "Email" button
 * on a B29 Stock Transfer, plus the Master Price List upload (starting-point
 * catalog: tiered pricing + carriage rules for the whole B29 catalog). */
const B29SettingsPanel = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadStatus = async () => {
    try {
      const res = await api.get(`${API}/b29-transfers/master-pricelist/status`);
      setStatus(res.data);
    } catch { /* leave blank */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`${API}/b29-transfers/settings/email`);
        setEmail(res.data?.email || '');
      } catch { /* leave blank */ }
      await loadStatus();
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`${API}/b29-transfers/settings/email`, { email });
      toast.success('B29 email address saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${API}/b29-transfers/master-pricelist/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data?.message || 'Master price list uploaded');
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload master price list');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" style={{ color: enconOrange }} /></div>;

  return (
    <div className="mb-5 space-y-3" data-testid="b29-settings-panel">
      <div className="p-4 rounded-2xl border-2 bg-orange-50/40 flex items-end gap-2 flex-wrap" style={{ borderColor: '#fde68a' }}>
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs text-gray-600 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> B29 Notification Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="b29@encon.co.uk"
            className="rounded-xl"
            data-testid="b29-settings-email-input"
          />
        </div>
        <Button onClick={save} disabled={saving} style={{ background: b29Gradient }} className="text-white rounded-full font-semibold" data-testid="b29-settings-email-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </Button>
      </div>

      <div className="p-4 rounded-2xl border-2 bg-yellow-50/40" style={{ borderColor: '#fde68a' }} data-testid="b29-master-pricelist-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-[240px]">
            <Label className="text-xs text-gray-600 flex items-center gap-1"><FileSpreadsheet className="w-3.5 h-3.5" /> B29 Master Price List</Label>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Upload the "Dashboard Master" workbook once as the starting-point catalog — tiered pricing
              (Tier 1-5 + Best Listed Price) and per-supplier carriage rules load into the B29 Pricing
              catalog below. Future single-supplier PDF/Excel uploads should follow this same column format.
            </p>
            {status?.meta ? (
              <p className="text-[11px] text-emerald-700 mt-1.5 flex items-center gap-1" data-testid="b29-master-pricelist-status">
                <CheckCircle2 className="w-3.5 h-3.5" /> Last upload: {status.meta.filename} — {status.product_count} products,
                {' '}{status.supplier_count} suppliers, carriage rules for {status.carriage_supplier_count} supplier(s)
              </p>
            ) : (
              <p className="text-[11px] text-gray-400 mt-1.5">No master price list uploaded yet.</p>
            )}
          </div>
          <div>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" data-testid="b29-master-pricelist-file-input" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 rounded-full text-white font-semibold text-sm px-4 py-2 shadow-md hover:scale-[1.03] hover:shadow-lg transition-all duration-200 active:scale-95 disabled:opacity-50"
              style={{ background: b29Gradient }}
              data-testid="b29-master-pricelist-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload Master Price List
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B29SettingsPanel;
